import React, { useState, useMemo, useCallback } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import { useFocusEffect } from '@react-navigation/native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectLeaseIds, selectAllLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetAutoPayStatusQuery, useCancelAutoPayMutation, useGetLeaseQuery, useGetPaymentMethodsQuery, leasesApi } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import { MobileAutopayDetailsModal } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import type { AutopayChange } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import { AutopayCard } from '@/components/residents/redesign/payments';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { SectionHeader } from '@/components/residents/redesign/SectionHeader';
import { GlassCard } from '@/components/residents/redesign/GlassCard';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getDisplayName, getInitials } from '@/utils/residents/utils';

// One arg shape for the card's subscription and the post-change refetch, so
// both address the same cache entry.
const leaseQueryArgs = (leaseId: number) => ({ leaseId, includeTransactions: false, includePayments: true });

/**
 * Mobile Autopay Section (Account Settings)
 * Uses the unified redesign AutopayCard components.
 */
export const MobileAutopaySection: React.FC = () => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useToast();
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Modals stay mounted after their first open so UnifiedModal can animate
  // them out; the lease id is kept through the exit.
  const [setupModal, setSetupModal] = useState<{ leaseId: number; visible: boolean } | null>(null);
  const [editModal, setEditModal] = useState<{ leaseId: number; visible: boolean } | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{ leaseId: number; autopayId: number } | null>(null);
  // Outlive the mutation (and the modal) on purpose: the card reads "Removing
  // autopay…" / "Setting up autopay…" until the refetched lease reflects the
  // change, so it never shows the old state in between.
  const [removingLeaseId, setRemovingLeaseId] = useState<number | null>(null);
  const [settingUpLeaseId, setSettingUpLeaseId] = useState<number | null>(null);

  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectAllLeaseIds);
  const connections = useAppSelector(linkedAcctsConnections);
  const connectionsFetchLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  const allLeaseIds = useMemo(() => {
    const combined = [...ownLeaseIds];
    connectionLeaseIds.forEach((id) => {
      if (!combined.includes(id)) combined.push(id);
    });
    return combined;
  }, [ownLeaseIds, connectionLeaseIds]);

  const connectionUserByLeaseId = useMemo(() => {
    const map = new Map<number, any>();
    (connections || []).forEach((conn: any) => {
      (conn?.leaseIds || []).forEach((id: number) => map.set(id, conn.user));
    });
    return map;
  }, [connections]);

  const { refetch: autoPayStatusRefetch, isLoading: autoPayStatusLoading } = useGetAutoPayStatusQuery();
  const { data: methodsData } = useGetPaymentMethodsQuery();
  const paymentMethods: any[] = useMemo(() => methodsData?.response || [], [methodsData]);

  const [cancelAutopay] = useCancelAutoPayMutation();

  const handleRefresh = useCallback(() => {
    autoPayStatusRefetch();
    setRefreshNonce((n) => n + 1);
  }, [autoPayStatusRefetch]);

  useFocusEffect(
    useCallback(() => {
      handleRefresh();
      return () => {};
    }, [handleRefresh])
  );

  const handleRemoveAutopay = useCallback((_leaseId: number, autopayId: number) => {
    setRemoveConfirm({ leaseId: _leaseId, autopayId });
  }, []);

  // Waits on the card's own cache entry, so a pending state ends on a lease
  // that reflects the change.
  const awaitLease = useCallback(
    (leaseId: number) =>
      dispatch(leasesApi.endpoints.getLease.initiate(leaseQueryArgs(leaseId), { subscribe: false, forceRefetch: true })),
    [dispatch],
  );

  const handleRemoveConfirmed = useCallback(async () => {
    if (!removeConfirm?.autopayId) return;
    const { leaseId, autopayId } = removeConfirm;
    setRemoveConfirm(null);
    setRemovingLeaseId(leaseId);
    try {
      await cancelAutopay(autopayId).unwrap();
      // The mutation already refreshes the autopay status; the lease queries
      // are refreshed here.
      dispatch(leasesApi.util.invalidateTags([{ type: 'Lease', id: leaseId }]));
      await awaitLease(leaseId);
      showSuccess(strings.account.autopayRemovedToast);
    } catch (error: any) {
      showError(error?.data?.message || error?.data?.error?.message || 'Failed to remove autopay');
    } finally {
      setRemovingLeaseId(null);
    }
  }, [removeConfirm, cancelAutopay, dispatch, awaitLease, showSuccess, showError]);

  // The modal has already invalidated the lease and autopay-status tags; a
  // setup or removal is held as pending until the card's lease lands, while
  // an edit keeps the modal open on the new values and just refreshes.
  const handleAutopayUpdated = useCallback(async (leaseId: number, change: AutopayChange) => {
    if (change === 'update') {
      handleRefresh();
      return;
    }
    const setPending = change === 'remove' ? setRemovingLeaseId : setSettingUpLeaseId;
    setPending(leaseId);
    try {
      await awaitLease(leaseId);
    } finally {
      setPending(null);
    }
  }, [awaitLease, handleRefresh]);

  const hasNoLeases = allLeaseIds.length === 0 && !connectionsFetchLoading;
  // First load only. A refetch (which the autopay modal triggers on save)
  // must not collapse the cards into a spinner, or the modal unmounts.
  const isLoading = connectionsFetchLoading || autoPayStatusLoading;

  if (isLoading) {
    return (
      <View style={styles.container}>
        <SectionHeader title="Autopay" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      </View>
    );
  }

  if (hasNoLeases) {
    return (
      <View style={styles.container}>
        <SectionHeader title="Autopay" />
        <GlassCard style={styles.emptyCard}>
          <View style={styles.emptyIconOuter}>
            <View style={styles.emptyIconGlow} />
            <View style={styles.emptyIconContainer}>
              {RESIDENTS_SVG_COMPONENTS.PAY && (
                <RESIDENTS_SVG_COMPONENTS.PAY width={24} height={24} color={colors.accent.green} />
              )}
            </View>
          </View>
          <Text style={[styles.emptyTitle, { color: colors.text.primary }]}>{strings.account.autopayEmptyTitle}</Text>
          <Text style={[styles.emptyMessage, { color: colors.text.muted }]}>{strings.account.autopayEmptyMessage}</Text>
        </GlassCard>
      </View>
    );
  }

  return (
    <>
      <View style={styles.container}>
        <SectionHeader title="Autopay" />
        <View style={styles.leasesList}>
          {allLeaseIds.map((leaseId) => (
            <LeaseAutopayItemCard
              key={leaseId}
              leaseId={leaseId}
              isOwnLease={ownLeaseIds.includes(leaseId)}
              connectionUser={connectionUserByLeaseId.get(leaseId)}
              authUser={authUser}
              paymentMethods={paymentMethods}
              refreshNonce={refreshNonce}
              isRemoving={removingLeaseId === leaseId}
              isSettingUp={settingUpLeaseId === leaseId}
              onSetup={() => setSetupModal({ leaseId, visible: true })}
              onEdit={() => setEditModal({ leaseId, visible: true })}
              onRemove={handleRemoveAutopay}
            />
          ))}
        </View>
      </View>

      {/* Remove Autopay Confirmation */}
      <ConfirmationModal
        visible={removeConfirm !== null}
        message="Are you sure you want to remove autopay? You will need to set it up again if you change your mind."
        onCancel={() => setRemoveConfirm(null)}
        onProceed={handleRemoveConfirmed}
        cancelText="No, cancel"
        proceedText="Yes, remove"
      />

      {/* Autopay Setup Modal */}
      {setupModal !== null && (
        <MobileAutopayDetailsModal
          visible={setupModal.visible}
          onClose={() => setSetupModal((m) => (m ? { ...m, visible: false } : m))}
          leaseId={setupModal.leaseId}
          onAutopayUpdated={(change) => handleAutopayUpdated(setupModal.leaseId, change)}
          mode="setup"
        />
      )}

      {/* Autopay Edit/View Modal */}
      {editModal !== null && (
        <MobileAutopayDetailsModal
          visible={editModal.visible}
          onClose={() => setEditModal((m) => (m ? { ...m, visible: false } : m))}
          leaseId={editModal.leaseId}
          onAutopayUpdated={(change) => handleAutopayUpdated(editModal.leaseId, change)}
          mode="view"
        />
      )}
    </>
  );
};

interface LeaseAutopayItemCardProps {
  leaseId: number;
  isOwnLease: boolean;
  connectionUser?: any;
  authUser?: any;
  paymentMethods: any[];
  refreshNonce: number;
  isRemoving: boolean;
  isSettingUp: boolean;
  onSetup: () => void;
  onEdit: () => void;
  onRemove: (leaseId: number, autopayId: number) => void;
}

const LeaseAutopayItemCard: React.FC<LeaseAutopayItemCardProps> = React.memo(({
  leaseId,
  isOwnLease,
  connectionUser,
  authUser,
  paymentMethods,
  refreshNonce,
  isRemoving,
  isSettingUp,
  onSetup,
  onEdit,
  onRemove,
}) => {
  const { data: leaseResp, refetch } = useGetLeaseQuery(leaseQueryArgs(leaseId), { skip: !leaseId });

  React.useEffect(() => {
    refetch();
  }, [refreshNonce, refetch]);

  const lease = (leaseResp as any)?.response?.lease;

  const propertyName = lease?.property?.marketingName || '—';
  const users = lease?.users || [];
  const primaryUser = users?.find((u: any) => u?.LeaseCustomer?.customerType === 'primary');
  const displayUser = isOwnLease
    ? (users?.find((u: any) => u?.id === authUser?.id) || primaryUser || users?.[0])
    : (connectionUser || primaryUser || users?.[0]);

  const firstName = displayUser?.firstName;
  const lastName = displayUser?.lastName;
  const photoUrl = displayUser?.photoUrl;
  const displayName = getDisplayName(firstName, lastName) || 'Resident';
  const initials = getInitials(firstName, lastName) || 'R';

  const recurringPayments = lease?.recurringPayments || [];
  const autopay = recurringPayments[0];
  const hasAutopay = Boolean(autopay);

  const paymentMethodLabel = useMemo(() => {
    if (!autopay?.sourceId) return '—';
    const method = paymentMethods.find((m: any) => {
      const methodSourceId = Number(m.sourceId) || m.id;
      return methodSourceId === Number(autopay.sourceId);
    });
    return method ? getPaymentMethodShortLabel(method) : '—';
  }, [autopay?.sourceId, paymentMethods]);

  const isSetupDisabled = useMemo(() => {
    const endDate = lease?.endDate ? new Date(lease.endDate) : null;
    if (!endDate || isNaN(endDate.getTime())) return false;
    const firstOfEndMonth = new Date(endDate);
    firstOfEndMonth.setDate(1);
    return firstOfEndMonth < new Date();
  }, [lease?.endDate]);

  if (!lease) return null;

  const formatDate = (iso?: string): string => {
    if (!iso) return 'End of lease';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  const amountText = autopay?.amount
    ? `$${Number(autopay.amount).toFixed(2)}`
    : (autopay?.payFullAmount !== false ? 'Full balance' : '—');

  return (
    <AutopayCard
      displayName={displayName}
      initials={initials}
      photoUrl={photoUrl}
      propertyName={propertyName}
      autopayEnabled={hasAutopay}
      amount={hasAutopay ? amountText : undefined}
      paymentMethodLabel={hasAutopay ? paymentMethodLabel : undefined}
      startDate={hasAutopay ? formatDate(autopay?.startDate || autopay?.createdAt) : undefined}
      endDate={hasAutopay ? formatDate(autopay?.endDate) : undefined}
      onSetup={onSetup}
      onEdit={onEdit}
      onRemove={hasAutopay ? () => onRemove(leaseId, autopay.id) : undefined}
      isSetupDisabled={isSetupDisabled}
      isRemoving={isRemoving}
      isSettingUp={isSettingUp}
    />
  );
});

LeaseAutopayItemCard.displayName = 'LeaseAutopayItemCard';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 8,
  },
  leasesList: {
    gap: 12,
  },
  loadingContainer: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyIconOuter: {
    width: 64,
    height: 64,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyIconGlow: {
    position: 'absolute',
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(102,206,102,0.06)',
  },
  emptyIconContainer: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyMessage: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
});

export default MobileAutopaySection;
